/**
* Push Notification Service
* Handles iOS (APNs via Capacitor) and web notifications.
*/
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import { keychainService } from './keychainService';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://worthly-backend-production.up.railway.app';

// Check if notifications are supported
export const isNotificationSupported = () => {
  if (Capacitor.isNativePlatform()) return true;
  return 'Notification' in window;
};

// Get current permission status
export const getNotificationPermission = () => {
  if (Capacitor.isNativePlatform()) {
    // On iOS, we check async — return 'default' as placeholder
    return 'default';
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// Check iOS permission status (async)
export const checkIOSPermission = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const result = await PushNotifications.checkPermissions();
    return result.receive; // 'prompt', 'granted', 'denied'
  } catch (e) {
    console.error('Error checking iOS push permission:', e);
    return null;
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await PushNotifications.requestPermissions();
      console.log('iOS push permission result:', result.receive);
      if (result.receive === 'granted') {
        await PushNotifications.register();
        return { success: true, permission: 'granted' };
      }
      return { success: false, permission: result.receive };
    } catch (error) {
      console.error('iOS push permission error:', error);
      return { success: false, permission: 'error', error };
    }
  }

  // Web fallback
  if (!('Notification' in window)) {
    return { success: false, permission: 'unsupported' };
  }
  if (Notification.permission === 'granted') {
    return { success: true, permission: 'granted' };
  }
  if (Notification.permission === 'denied') {
    return { success: false, permission: 'denied' };
  }
  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    return { success: false, permission: 'error', error };
  }
};

// Set up iOS push notification listeners
export const setupIOSPushListeners = (userId, onTokenReceived) => {
  if (!Capacitor.isNativePlatform()) return;

  // Successful registration — we get the real APNs token
  PushNotifications.addListener('registration', async (token) => {
    console.log('APNs token received:', token.value);
    await registerDeviceToken(userId, token.value, 'ios');
    if (onTokenReceived) onTokenReceived(token.value);
  });

  // Registration failed
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration failed:', error);
  });

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push received in foreground:', notification);
  });

  // User tapped on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('Push notification tapped:', notification);
  });
};

// Register device token with backend
export const registerDeviceToken = async (userId, token, platform = 'web') => {
  try {
    const sessionToken = await keychainService.getItem('session_token');
    console.log('[registerDeviceToken] userId:', userId, 'platform:', platform, 'hasSessionToken:', !!sessionToken);

    if (!sessionToken) {
      console.error('[registerDeviceToken] No session_token! Aborting.');
      return { success: false, error: 'no_session_token' };
    }
    if (!userId) {
      console.error('[registerDeviceToken] No userId! Aborting.');
      return { success: false, error: 'no_user_id' };
    }

    const body = {
      user_id: userId,
      device_token: token,
      platform: platform,
      registered_at: new Date().toISOString()
    };
    console.log('[registerDeviceToken] Sending to backend:', JSON.stringify(body));

    const response = await fetch(`${API_URL}/api/notifications/register-device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify(body)
    });

    const responseData = await response.json();
    console.log('[registerDeviceToken] Response status:', response.status, 'data:', JSON.stringify(responseData));

    if (!response.ok) {
      console.error('[registerDeviceToken] Backend returned error:', response.status, responseData);
      return { success: false, error: responseData };
    }

    return responseData;
  } catch (error) {
    console.error('[registerDeviceToken] Exception:', error.message || error);
    return { success: false, error: error.message || error };
  }
};

// Update notification preferences
export const updateNotificationPreferences = async (userId, preferences) => {
  try {
    const sessionToken = await keychainService.getItem('session_token');
    const response = await fetch(`${API_URL}/api/notifications/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify({
        user_id: userId,
        preferences: preferences
      })
    });
    if (!response.ok) throw new Error('Failed to update preferences');
    return await response.json();
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    return { success: false, error };
  }
};
